<template>
  <DsPageContent>
    <DsHero
      v-if="content"
      :title="content.title"
      theme="grey"
      :navigation-data="backLink"
      :with-title-size-large="false"
    />
    <KnowledgeHubFilters
      :initial-filters="initialFilters"
      @selected="handleFiltersUpdate"
    />

    <DsSection v-if="isLoading">
      <DsPlaceholderLoader />
    </DsSection>

    <template v-else>
      <DsSection
        v-if="_articles && _articles.length"
        :with-space="false"
        class="ds-padding-y-48"
      >
        <DsGrid
          :items="_articles"
          :column-width="{ small: 12, medium: 6, large: 4 }"
        >
          <template #default="{ item }">
            <DsCardBlog
              class="kh-article-card ds-md-margin-bottom-24"
              :image="item.image"
              :image-with-sizes="item.imageWithSizes"
              :title="item.title"
              :tag="item.tag"
              :link="item.link"
              :subtitle="`${item.dateFormatted} · ${t('minutesAmount', item.minutesToRead)}`"
              aspect-ratio="16/9"
              show-placeholder-on-image-error
            />
          </template>
        </DsGrid>
        <DsPlaceholderLoader v-if="isLoadingMore" />
        <div class="ds-grid">
          <div class="ds-grid__col ds-grid__col--12">
            <button
              v-if="canLoadMore"
              class="ds-button ds-button--secondary ds-button--black ds-button-link ds-margin-top-48"
              :class="{ 'ds-button-link--disabled': isLoadingMore }"
              @click="loadMore"
            >
              {{ labels.loadMoreArticlesButton }}
            </button>
          </div>
        </div>
      </DsSection>
      <DsSection v-else>
        <div class="ds-grid">
          <div class="ds-grid__col ds-grid__col--12">
            <h2 class="ds-heading-xsmall">
              {{ content.noResultsTitle }}
            </h2>
            <p>
              {{ content.noResultsSubtitle }}
            </p>
          </div>
        </div>
      </DsSection>
    </template>
  </DsPageContent>
</template>
<script setup>
import { DsPageContent, DsHero, DsSection, DsPlaceholderLoader, DsGrid, DsCardBlog } from 'awds'
import { PageKnowledgeHubArticles } from '../../../../core/data-layer/definitions/knowledgeHub'
import KnowledgeHubFilters from '../../../components/knowledge-hub/knowledge-hub-filters.vue'
import { mapperKnowledgeHubArticlesSummaries } from '../../../../core/data-layer/knowledge-hub/summary/mapper-knowledge-hub-articles-summaries'
import buildQuery from '../../../../core/data-layer/pages/page-knowledge-hub-articles/query-page-knowledge-hub-articles'

const { t } = useI18n()
const { queryContentful } = useQueries()

const { labels } = useKnowledgeHubGlobalData()
const { locale: localeRef } = useI18n()
const { getLocaleIso } = useLocales()
const { useStandardAsyncData, onBeforeAdyenData, onAfterAdyenData, content } = useKnowledgeHubPageData()

const route = useRoute()
const { getSlug, getParentPath } = usePageUtils()
const slug = getSlug(route.path) || 'articles'

const store = useRoutesStore()
const router = useRouter()

const LIMIT = 18

await onBeforeAdyenData({
  globalDataContentType: 'PageKnowledgeHubArticles'
})

const filtersToQuery = (filters) =>
  filters.reduce(
    (prevValue, filter) => ({
      ...prevValue,
      [filter.name]: filter.value
    }),
    {}
  )
const queryToFilters = (query) =>
  Object.entries(query).map(([name, value]) => {
    if (['industries', 'topics', 'products'].includes(name) && !Array.isArray(value)) {
      return { name, value: [value] }
    }
    return { name, value }
  })

const { data } = await useAsyncData('knowledge-hub-articles' + route.params.slug + '-' + localeRef.value, async () => {
  const page = await useStandardAsyncData(PageKnowledgeHubArticles, {})
  if (!page || !Object.keys(page).length) {
    throw createError({ statusCode: 404, message: 'Page not found' })
  }

  const { pageData } = page

  const slugParent = getParentPath(route.path)

  return {
    pageData,
    slugParent
  }
})

const page = ref(0)
const _articles = ref([])
const initialFilters = ref([])
const isLoading = ref(false)
const isLoadingMore = ref(false)
const _total = ref(1)

const parentPageUrl = computed(() => {
  return data.value?.slugParent
})

const backLink = computed(() => {
  return { text: labels.value.backLinkTitle || 'Knowledge Hub', url: parentPageUrl.value }
})

const canLoadMore = computed(() => {
  return _total.value > _articles.value.length
})

onMounted(() => {
  const { query } = route
  const calculatedInitialFilters = queryToFilters(query)
  initialFilters.value = calculatedInitialFilters

  updateData(filtersToQuery(calculatedInitialFilters))
})

onBeforeRouteUpdate((to, from, next) => {
  const { query } = to
  updateData(query)
  next()
})

const handleFiltersUpdate = (updatedFilters) => {
  const queryValues = filtersToQuery(updatedFilters)
  if (JSON.stringify(route.query) !== JSON.stringify(queryValues)) {
    router.push({ query: queryValues })
  }
}

const fetchArticles = async (queryParams) => {
  const { articleType, products, industries, topics } = queryParams
  const locale = getLocaleIso(localeRef.value)
  const gqlQuery = buildQuery(articleType ? [articleType] : [], products, industries, topics)
  const variables = {
    locale,
    skip: page.value * LIMIT,
    limit: LIMIT,
    slug,
    preview: false
  }

  const { articles } = await queryContentful(
    {
      query: gqlQuery,
      variables
    },
    { log: false }
  )

  const articleItems = articles.items
  const articleTotal = articles.total

  const imageSizes = {
    small: { width: 368, height: 207, aspectRatio: '16/9' },
    medium: { width: 324, height: 182, aspectRatio: '16/9' },
    large: { width: 327, height: 184, aspectRatio: '16/9' }
  }

  const { getUrl, getInternalUrl } = useRoutes(store.routes, localeRef)

  const options = {
    getUrl,
    getInternalUrl,
    locale: localeRef.value,
    imageOptions: {
      resize: true,
      lazy: true,
      sizes: imageSizes
    }
  }

  return {
    articles: mapperKnowledgeHubArticlesSummaries(articleItems, options),
    total: articleTotal
  }
}

const updateData = async (queryParams) => {
  isLoading.value = true
  page.value = 0
  try {
    const { articles, total } = await fetchArticles(queryParams)
    _articles.value = articles
    _total.value = total
    isLoading.value = false
  } catch (e) {
    isLoading.value = false
    // eslint-disable-next-line no-console
    console.error(e)
    throw new Error(`Query failed! - ${e}`)
  }
}

const loadMore = async () => {
  isLoadingMore.value = true
  page.value = page.value + 1
  try {
    const { query } = route
    const { articles } = await fetchArticles(query)
    isLoadingMore.value = false
    _articles.value = [..._articles.value, ...articles]
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e)
    throw new Error(`Query failed! - ${e}`)
  }
}

await onAfterAdyenData(data.value)
</script>
<style lang="scss" scoped>
@import 'awds/foundation-vars.scss';

/* Prevent misalignment: Add a fixed max height to the card to prevent issues when using SVGs that are not transformed using fixed width and height */
.kh-article-card :deep(.ds-blog-card__image img) {
  max-height: 282px;
}
</style>
